import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AppRoutesDefinitions } from './app.routes';
import { AuthGuard } from '@app/core/guards/auth.guard';
import { UserRole } from '@shared/models/role';
import { MainLayout } from '@app/layouts/main';
import { AuthLayout } from '@app/layouts/auth';
import { NonAuthGuard } from '@core/guards/non-auth.guard';
import { signupFlowAccessGuard } from '@modules/components/auth/signup-flow/guards/signup-flow-access.guard';
import { newSubjectAccountGuard } from '@pages/new-subject/guards/new-subject-account.guard';
import { SurveyLayout } from './layouts/survey';
import { PublicMainLayout } from '@app/layouts/public-main';
import { stateResolver } from './core/guards/state.resolver';
import { EmptyLayout } from './layouts/empty';
import { testRunnerParamsGuard } from './core/guards/test-params.guard';
import { studentsRoleMatchGuard } from '@core/guards/students-redirect.guard';
import { TeacherNotResignedStatuses } from '@modules/components/schedule/dto/teacher.dto';
import {
  STUDENTS_QUEUE_STAFF_ROLES,
  STUDENTS_QUEUE_TEACHER_STATUSES,
} from '@shared/services/students-queue/students-queue.constants';
import { addDiagnosticLessonGuard } from '@app/core/guards/add-diagnostic-lesson.guard';
import { sellLessonPackageGuard } from '@app/core/guards/sell-lesson-package.guard';
import { payFirstLessonResolver } from '@pages/follow-up/pay-first-lesson.resolver';
import { LessonsRoutesEnum } from '@pages/lessons/lessons.routes.enum';
import { TeacherPages } from '@pages/teacher/teacher-pages.enum';
import {
  QUIZ_ASSIGNMENT_ID_PARAM,
  QUIZ_JOIN_SEGMENT,
  QUIZ_PREPARATION_ID_PARAM,
  QUIZ_RUN_SEGMENT,
} from '@pages/quizzes/quizzes-routes.constants';
import { ReportBuilderPages } from '@pages/report-builder/report-builder-pages.enum';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: AppRoutesDefinitions.LOGIN,
  },
  {
    path: '',
    component: AuthLayout,
    canActivate: [NonAuthGuard],
    children: [
      {
        path: AppRoutesDefinitions.LOGIN,
        loadComponent: () =>
          import('@modules/components/auth/signin/sign-in.component').then(
            (c) => c.SignInComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.LOGIN_VIA_PROVIDER,
        loadComponent: () =>
          import('@modules/components/auth/provider/provider.component').then(
            (c) => c.ProviderComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.PASSWORD_RESTORE,
        loadComponent: () =>
          import(
            '@modules/components/auth/password-restore/password-restore.component'
          ).then((c) => c.PasswordRestoreComponent),
      },
    ],
  },
  {
    path: '',
    component: AuthLayout,
    children: [
      {
        path: AppRoutesDefinitions.SIGNUP,
        canActivate: [signupFlowAccessGuard],
        data: { eventBased: true },
        loadComponent: () =>
          import(
            '@modules/components/auth/signup-flow/signup-flow.component'
          ).then((c) => c.SignupFlowComponent),
      },
    ],
  },
  {
    path: '',
    component: SurveyLayout,
    children: [
      {
        path: AppRoutesDefinitions.SURVEYS,
        loadChildren: () =>
          import('@pages/surveys/surveys.routes').then(
            (feature) => feature.surveysRoutes,
          ),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.PUBLIC}/${AppRoutesDefinitions.QUIZ}`,
    component: EmptyLayout,
    loadChildren: () =>
      import('@pages/public/pages/marketing-quiz/marketing-quiz.routes').then(
        (feature) => feature.marketingQuizRoutes,
      ),
  },
  {
    path: `${AppRoutesDefinitions.PUBLIC}/${AppRoutesDefinitions.CONTINUE}`,
    component: EmptyLayout,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/public/pages/continue/public-continue.component').then(
            (c) => c.PublicContinueComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.PUBLIC,
    component: PublicMainLayout,
    loadChildren: () =>
      import('@pages/public/public.routes').then(
        (feature) => feature.publicRoutes,
      ),
  },
  {
    path: AppRoutesDefinitions.WHITEBOARD,
    component: EmptyLayout,
    loadChildren: () =>
      import('@pages/whiteboard/whiteboard-routes').then(
        (feature) => feature.whiteboardRoutes,
      ),
  },
  {
    path: AppRoutesDefinitions.STUDENT_PAIR,
    component: EmptyLayout,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/student-pair/student-pair.component').then(
            (c) => c.StudentPairComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.AUTH_LINK,
    component: EmptyLayout,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/auth-link/auth-link.component').then(
            (c) => c.AuthLinkComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.ACCOUNT_SWITCH,
    component: EmptyLayout,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/account-switch/account-switch.component').then(
            (c) => c.AccountSwitchComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.TEST_RUNNER,
    component: EmptyLayout,
    canActivate: [AuthGuard, testRunnerParamsGuard],
    data: {
      roles: [UserRole.TEACHER],
      allowedTeacherStatuses: TeacherNotResignedStatuses,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/test-runner/test-runner.component').then(
            (c) => c.TestRunnerComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.INTERNSHIP_START,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    resolve: { state: stateResolver },
    data: {
      roles: [UserRole.TEACHER],
      allowedTeacherStatuses: TeacherNotResignedStatuses,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/internship-start/internship-start.component').then(
            (c) => c.InternshipStartComponent,
          ),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.CLIENT_BOOKING_FLOW,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: { roles: [UserRole.CLIENT] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@pages/client-booking-flow/client-booking-flow.component'
          ).then((c) => c.ClientBookingFlowComponent),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.NEW_SUBJECT,
    component: EmptyLayout,
    canActivate: [AuthGuard, newSubjectAccountGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/new-subject/new-subject.component').then(
            (c) => c.NewSubjectComponent,
          ),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.SCHEDULE_CHANGE}/:token`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRole.CLIENT,
        UserRole.ADMIN,
        UserRole.MANAGER,
        UserRole.RECRUITER,
        UserRole.SALES_MANAGER,
        UserRole.SUPPORT_MANAGER,
      ],
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@pages/schedule-change-link/schedule-change-link.component'
          ).then((c) => c.ScheduleChangeLinkComponent),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.TEACHER_SCHEDULE_CHANGE,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.TEACHER],
      allowedTeacherStatuses: TeacherNotResignedStatuses,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@pages/teacher-schedule-change/teacher-schedule-change.component'
          ).then((c) => c.TeacherScheduleChangeComponent),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.ADD_DIAGNOSTIC_LESSON,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.SALES_MANAGER],
    },
    children: [
      {
        path: '',
        canActivate: [addDiagnosticLessonGuard],
        loadComponent: () =>
          import(
            '@pages/add-diagnostic-lesson/add-diagnostic-lesson.component'
          ).then((c) => c.AddDiagnosticLessonComponent),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.LESSONS}/:id/${LessonsRoutesEnum.MOVE}`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRole.CLIENT,
        UserRole.TEACHER,
        UserRole.ADMIN,
        UserRole.MANAGER,
        UserRole.RECRUITER,
        UserRole.SALES_MANAGER,
        UserRole.SUPPORT_MANAGER,
      ],
      allowedTeacherStatuses: TeacherNotResignedStatuses,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/move-lesson/move-lesson.component').then(
            (c) => c.MoveLessonComponent,
          ),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.BACKGROUND_CHECKS}/:checkId`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.SUPPORT_MANAGER],
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@pages/background-check-review/background-check-review.component'
          ).then((c) => c.BackgroundCheckReviewComponent),
      },
    ],
  },
  {
    path: AppRoutesDefinitions.SELL_LESSON_PACKAGE,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRole.ADMIN,
        UserRole.MANAGER,
        UserRole.RECRUITER,
        UserRole.SALES_MANAGER,
      ],
    },
    children: [
      {
        path: '',
        canActivate: [sellLessonPackageGuard],
        loadComponent: () =>
          import(
            '@pages/sell-lesson-package/sell-lesson-package.component'
          ).then((c) => c.SellLessonPackageComponent),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.FOLLOW_UP}/${AppRoutesDefinitions.PAY_FIRST_LESSON}/:studentId`,
    component: MainLayout,
    canActivate: [AuthGuard],
    data: { roles: [UserRole.CLIENT] },
    resolve: { followUp: payFirstLessonResolver },
  },
  {
    path: '',
    loadChildren: () =>
      import(
        '@pages/regular-teacher-change/regular-teacher-change-routing.module'
      ).then((m) => m.regularTeacherChangeRoutes),
  },
  {
    path: `${AppRoutesDefinitions.QUIZZES}/fullscreen`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.TEACHER, UserRole.CLIENT],
      allowedTeacherStatuses: TeacherNotResignedStatuses,
    },
    children: [
      {
        path: ':id',
        loadComponent: () =>
          import(
            '@pages/quizzes/quiz-fullscreen/quiz-fullscreen.component'
          ).then((c) => c.QuizFullscreenComponent),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.QUIZZES}/${QUIZ_RUN_SEGMENT}/:${QUIZ_ASSIGNMENT_ID_PARAM}`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    data: { roles: [UserRole.STUDENT, UserRole.CLIENT] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/quiz-runner/quiz-runner.component').then(
            (c) => c.QuizRunnerComponent,
          ),
      },
    ],
  },
  {
    path: `${AppRoutesDefinitions.QUIZZES}/${QUIZ_JOIN_SEGMENT}/:${QUIZ_PREPARATION_ID_PARAM}`,
    component: EmptyLayout,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/quiz-join/quiz-join.component').then(
            (c) => c.QuizJoinComponent,
          ),
      },
    ],
  },
  {
    path: '',
    component: MainLayout,
    resolve: { state: stateResolver },
    children: [
      {
        path: AppRoutesDefinitions.FREE_HOURS,
        loadComponent: () =>
          import('@pages/free-hours/free-hours.component').then(
            (c) => c.FreeHoursComponent,
          ),
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
      },
      {
        path: AppRoutesDefinitions.TEACHERS_QUEUE,
        loadComponent: () =>
          import(
            '@pages/free-hours/pages/teachers-queue/teachers-queue.component'
          ).then((c) => c.TeachersQueueComponent),
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
      },
      {
        path: AppRoutesDefinitions.CALENDAR,
        loadChildren: () =>
          import('@modules/components/schedule/schedule.module').then(
            (m) => m.ScheduleModule,
          ),
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
            UserRole.TEACHER,
            UserRole.CLIENT,
            UserRole.STUDENT,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
      },
      {
        path: `${AppRoutesDefinitions.CALENDAR}/:contact`,
        loadChildren: () =>
          import('@modules/components/schedule/schedule.module').then(
            (m) => m.ScheduleModule,
          ),
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.TEACHER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
      },
      {
        path: AppRoutesDefinitions.DEAL,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadChildren: () =>
          import('./modules/components/deal/deal.module').then(
            (m) => m.DealModule,
          ),
      },
      {
        path: AppRoutesDefinitions.FREE_SLOTS,
        redirectTo: `/${AppRoutesDefinitions.TEACHER}/${TeacherPages.WORKING_TIME}`,
        pathMatch: 'full',
      },
      {
        path: AppRoutesDefinitions.USERS,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadComponent: () =>
          import('@pages/admin-panel/pages/users/users.component').then(
            (c) => c.UsersComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.STUDENTS,
        canActivate: [AuthGuard],
        canMatch: [
          studentsRoleMatchGuard(
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ),
        ],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadComponent: () =>
          import('@pages/admin-panel/pages/students/students.component').then(
            (c) => c.StudentsComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.STUDENTS_QUEUE,
        canActivate: [AuthGuard],
        canMatch: [studentsRoleMatchGuard(...STUDENTS_QUEUE_STAFF_ROLES)],
        data: { roles: STUDENTS_QUEUE_STAFF_ROLES },
        loadComponent: () =>
          import(
            '@pages/students-queue/staff-students-queue/staff-students-queue.component'
          ).then((c) => c.StaffStudentsQueueComponent),
      },
      {
        path: AppRoutesDefinitions.STUDENTS_QUEUE,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.TEACHER],
          allowedTeacherStatuses: STUDENTS_QUEUE_TEACHER_STATUSES,
        },
        loadComponent: () =>
          import(
            '@pages/students-queue/teacher-students-queue/teacher-students-queue.component'
          ).then((c) => c.TeacherStudentsQueueComponent),
      },
      {
        path: AppRoutesDefinitions.STUDENTS,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.TEACHER],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadComponent: () =>
          import('@pages/teacher-students/teacher-students.component').then(
            (c) => c.TeacherStudentsComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.STUDENT_JOURNAL,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
            UserRole.TEACHER,
            UserRole.CLIENT,
            UserRole.STUDENT,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import(
            './modules/components/student-journal/student-journal.module'
          ).then((m) => m.StudentJournalModule),
      },
      {
        path: AppRoutesDefinitions.CLIENT_PROFILE,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.CLIENT,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadChildren: () =>
          import(
            './modules/components/client-profile/client-profile.module'
          ).then((m) => m.ClientProfileModule),
      },
      {
        path: AppRoutesDefinitions.NOTIFICATIONS,
        canActivate: [AuthGuard],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/notifications/notifications.component').then(
            (c) => c.NotificationsComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.DASHBOARD,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.CLIENT,
            UserRole.TEACHER,
            UserRole.ADMIN,
            UserRole.SALES_MANAGER,
          ],
        },
        loadComponent: () =>
          import('@pages/dashboard/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.PAIR,
        canActivate: [AuthGuard],
        data: { roles: [UserRole.CLIENT] },
        loadComponent: () =>
          import('@pages/pair/pair.component').then((c) => c.PairComponent),
      },
      {
        path: AppRoutesDefinitions.TEACHER_SEARCH,
        canActivate: [AuthGuard],
        data: { roles: [UserRole.CLIENT] },
        loadChildren: () =>
          import('@pages/teacher-search/teacher-search-routes').then(
            (feature) => feature.teacherSearchRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.AI_SEARCH,
        canActivate: [AuthGuard],
        data: { roles: [UserRole.CLIENT] },
        loadChildren: () =>
          import('@pages/ai-search/ai-search-routes').then(
            (feature) => feature.aiSearchRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.QUIZZES,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.CLIENT, UserRole.STUDENT, UserRole.TEACHER],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/quizzes/quizzes-routes').then(
            (feature) => feature.quizzesRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.LESSON_PREPARATION,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.TEACHER, UserRole.ADMIN],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/lesson-preparation/lesson-preparation-routes').then(
            (feature) => feature.lessonPreparationRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.MY_BOARDS,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.TEACHER, UserRole.STUDENT, UserRole.CLIENT],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/my-boards/my-boards-routes').then(
            (feature) => feature.myBoardsRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.LESSON_PREPARATION_LIBRARY,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.TEACHER,
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import(
            '@pages/lesson-preparation-library/lesson-preparation-library-routes'
          ).then((feature) => feature.lessonPreparationLibraryRoutes),
      },
      {
        path: AppRoutesDefinitions.LESSON_REPORT,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.TEACHER,
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
            UserRole.CLIENT,
            UserRole.STUDENT,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/lesson-report/lesson-report-routes').then(
            (feature) => feature.lessonReportRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.REPORTS,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadChildren: () =>
          import('@pages/manager-reports/reports-routes').then(
            (feature) => feature.reportsRoutes,
          ),
      },
      {
        path: ReportBuilderPages.ROOT,
        loadChildren: () =>
          import('@pages/report-builder/report-builder-routes').then(
            (feature) => feature.reportBuilderRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.SETTINGS,
        canActivate: [AuthGuard],
        data: {
          roles: [],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/settings/settings-routes').then(
            (feature) => feature.settingsRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.TEACHER,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.TEACHER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadChildren: () =>
          import('@pages/teacher/teacher-routes').then(
            (feature) => feature.teacherRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.AI_TUTOR,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.CLIENT,
            UserRole.STUDENT,
            UserRole.TEACHER,
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadComponent: () =>
          import('@pages/ai-tutor/ai-tutor.component').then(
            (c) => c.AITutorComponent,
          ),
      },
      {
        path: `${AppRoutesDefinitions.AI_TUTOR}/:id`,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.CLIENT,
            UserRole.STUDENT,
            UserRole.TEACHER,
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadComponent: () =>
          import('@pages/ai-tutor/ai-tutor.component').then(
            (c) => c.AITutorComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.CHAT,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.TEACHER, UserRole.CLIENT, UserRole.STUDENT],
          allowedTeacherStatuses: TeacherNotResignedStatuses,
        },
        loadChildren: () =>
          import('@pages/chat/chat.routes').then(
            (feature) => feature.chatRoutes,
          ),
      },
      {
        path: AppRoutesDefinitions.REFERRALS,
        canActivate: [AuthGuard],
        data: { roles: [UserRole.CLIENT, UserRole.ADMIN] },
        loadComponent: () =>
          import('@pages/referrals/referrals.component').then(
            (c) => c.ReferralsComponent,
          ),
      },
      {
        path: AppRoutesDefinitions.ADMIN_PANEL,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRole.ADMIN,
            UserRole.MANAGER,
            UserRole.RECRUITER,
            UserRole.SALES_MANAGER,
            UserRole.SUPPORT_MANAGER,
          ],
        },
        loadChildren: () =>
          import('@pages/admin-panel/admin-panel-routes').then(
            (feature) => feature.adminPanelRoutes,
          ),
      },
      {
        path: '**',
        redirectTo: AppRoutesDefinitions.FREE_HOURS,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
